import { Event, EventEmitter, Element } from "@stencil/core";
import {
  Component,
  ComponentInterface,
  h,
  Host,
  Prop,
  State,
  Watch,
} from "@stencil/core";
import {
  getFormattedDate,
  isDateDisabled,
  daysInMonth,
  getFirstDayOfMonth,
  isCurrentMonth,
  monthNames,
  weekDays,
  moveContainerPosition,
  getFormattedDateWithSlashes,
  isStringDateValid,
  getDateParts,
  getLastDayOfMonth,
  getDaysInNextMonth,
} from "../../utils/datePickerUtils";

@Component({
  tag: "signet-date-range",
  styleUrl: "signet-date-range-picker.scss",
  shadow: false,
})
export class SignetDateRange implements ComponentInterface {
  @Element() el!: HTMLElement;
  @State() startDateValue: Date | null = null;
  @State() endDateValue: Date | null = null;
  @State() dummyDateValue: Date | null = null;
  @State() defaultDateValue: Date | null = null;
  @State() formattedStartDateValue = "";
  @State() formattedEndDateValue = "";
  @State() currentMonth: number;
  @State() currentYear: number;
  @State() showCalendar = false;
  @State() placeholder = "MM/DD/YYYY";
  @State() isEndInputFocused = false;
  @State() calendarContainerElement?: HTMLElement;
  @State() startDateInputElement?: HTMLInputElement;
  @State() endDateInputElement?: HTMLInputElement;
  @State() isStartDateInputHovered? = false;
  @State() isEndDateInputHovered? = false;
  @State() yearRangeStart: number;
  @State() view: "years" | "months" | "days" = "days";
  @State() isStartDateValid: boolean;
  @State() isEndDateValid: boolean;
  @State() invalidStartDate = "";
  @State() invalidEndDate = "";
  @State() startDateError: boolean;
  @State() endDateError: boolean;

  /**
   * The start date of the date range in string format (e.g., 'MM/DD/YYYY')
   */
  @Prop() startDate: string;
  /**
   * The end date of the date range in string format (e.g., 'MM/DD/YYYY')
   */
  @Prop() endDate: string;
  /**
   * A boolean indicating whether the date range picker is disabled
   */
  @Prop() disabled = false;
  /**
   * The locale for formatting the dates (e.g., 'en-US', 'en-GB')
   */
  @Prop() locale = "en-US";
  /**
   * A boolean indicating whether the end date should be automatically filled with the start date when a start date is selected
   */
  @Prop() autoFillEndDate = false;
  /**
   * The minimum selectable date the date range in string format (e.g., 'MM/DD/YYYY')
   */
  @Prop() minDate: string;
  /**
   * The maximun selectable date of the date range in string format (e.g., 'MM/DD/YYYY')
   */
  @Prop() maxDate: string;
  /**
   * The dummy date of the date range in string format (e.g., 'MM/DD/YYYY')
   */
  @Prop() dummyDate: string;
  /**
   * The default date value of the calendar (e.g., 'MM/DD/YYYY')
   */
  @Prop() defaultValue: string;
  /**
   * A boolean indicating whether the date range picker is in a warning state with yellow border color.
   */
  @Prop() warning: boolean;
  /**
   * A boolean indicating whether the date range picker is in an error state with red border color.
   */
  @Prop() error: boolean;
  /**
   * The label for the start date input field
   */
  @Prop() startDateLabel = "Start Date";
  /**
   * The label for the end date input field
   */
  @Prop() endDateLabel = "End Date";
  /**
   * A boolean indicating whether to show clear button
   */
  @Prop() clearable = false;
  /**
   * If true, the date range picker is required and a colored asterisk will be shown in the labels.
   */
  @Prop() required = false;
  /**
   * The color of the required asterisk in the label. Defaults to 'red'.
   */
  @Prop() requiredAsteriskColor = 'red';
  /**
   * An event emitter that fires when the selected start or end date changes. It emits an object containing the startDate and endDate values
   */
  @Event() changeEvent: EventEmitter<{
    startDate: string;
    endDate: string;
  }>;
  /**
   * An event emitter that fires when the date range picker loses focus.
   */
  @Event({ bubbles: true, composed: true })
  handleBlur: EventEmitter<FocusEvent>;
  /**
   * This event is emitted when the input element of the date picker gains focus. It can be used to perform actions or handle events when the date picker is focused, such as showing a calendar or changing the input format.
   */
  @Event({ bubbles: true, composed: true })
  handleFocus: EventEmitter<FocusEvent>;

  /**
   * An event emitter that fires when an invalid date is entered.
   * It emits an error object containing details about the invalid input.
   */
  @Event() dateError: EventEmitter<{
    message: string;
    invalidDate: string;
    field: "startDate" | "endDate";
    timestamp: Date;
  }>;

  /**
   * The size of the date range picker inputs. Can be 'sm' | 'small', 'md' | 'medium', or 'lg' | 'large'.
   * Affects the input's padding and height.
   * - sm/small: 32px
   * - md/medium: 42px (default)
   * - lg/large: 52px
   */
  @Prop() size: 'sm' | 'small' | 'md' | 'medium' | 'lg' | 'large' = 'medium';

  private getNormalizedSize() {
    switch (this.size) {
      case 'sm':
      case 'small':
        return 'small';
      case 'lg':
      case 'large':
        return 'large';
      case 'md':
      case 'medium':
      default:
        return 'medium';
    }
  }

  @Watch("showCalendar")
  @Watch("isEndInputFocused")
  @Watch("startDateValue")
  @Watch("defaultDateValue")
  updateCalendarUiDefaultMonthAndYearValues() {
    let dateToDisplayInCalendarUI = new Date();

    if (this.defaultDateValue) {
      dateToDisplayInCalendarUI = this.defaultDateValue;
    }

    if (this.startDateValue) {
      dateToDisplayInCalendarUI = this.startDateValue;
    }

    this.currentMonth = dateToDisplayInCalendarUI.getMonth();
    this.currentYear = dateToDisplayInCalendarUI.getFullYear();
    this.yearRangeStart =
      Math.floor(dateToDisplayInCalendarUI.getFullYear() / 10) * 10;
  }

  @Watch("startDate")
  updateStartDateValue(newValue: string) {
    this.startDateValue = newValue ? new Date(newValue) : null;
  }

  @Watch("endDate")
  updateEndDateValue(newValue: string) {
    this.endDateValue = newValue ? new Date(newValue) : null;
  }

  @Watch("startDateValue")
  setFormatToStartDateValue() {
    this.formattedStartDateValue = getFormattedDate(
      this.startDateValue,
      this.locale
    );

    if (this.startDateInputElement) {
      this.startDateInputElement.value = this.formattedStartDateValue;
    }
  }

  @Watch("endDateValue")
  setFormatToEndDateValue() {
    this.formattedEndDateValue = getFormattedDate(
      this.endDateValue,
      this.locale
    );

    if (this.endDateInputElement) {
      this.endDateInputElement.value = this.formattedEndDateValue;
    }
  }

  @Watch("dummyDate")
  updateDummyDateValue(newValue: string) {
    this.dummyDateValue = newValue ? new Date(newValue) : null;
  }

  @Watch("defaultValue")
  updateDefaultValue(newValue: string) {
    this.defaultDateValue = newValue ? new Date(newValue) : null;
  }

  private updatedDateValues() {
    this.startDate = this.startDateValue
      ? this.startDateValue.toISOString()
      : "";
    this.endDate = this.endDateValue ? this.endDateValue.toISOString() : "";

    this.emitChangeEvent();

    // Vue 2: emit 'input' event
    this.el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: {
        startDate: this.startDate,
        endDate: this.endDate
      }
    }));

    // Vue 3: emit 'update:modelValue' event
    this.el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: {
        startDate: this.startDate,
        endDate: this.endDate
      }
    }));
  }

  componentWillLoad() {
    if (this.locale === "en-GB") {
      this.placeholder = "DD/MM/YYYY";
    } else if (this.locale === "en-US") {
      this.placeholder = "MM/DD/YYYY";
    }

    this.updateStartDateValue(this.startDate);
    this.updateEndDateValue(this.endDate);
    this.updateDummyDateValue(this.dummyDate);
    this.updateDefaultValue(this.defaultValue);
    this.runValidationRules();
  }

  componentDidLoad() {
    this.moveCalendarContainerToDocumentBody();
  }

  private moveCalendarContainerToDocumentBody() {
    if (this.calendarContainerElement) {
      document.body.appendChild(this.calendarContainerElement);
    }
  }

  private showNextMonth() {
    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
  }

  private showPreviousMonth() {
    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
  }

  private emitChangeEvent() {
    this.changeEvent.emit({
      startDate: getFormattedDate(this.startDateValue, this.locale),
      endDate: getFormattedDate(this.endDateValue, this.locale),
    });
    // console.log(getFormattedDate(this.startDateValue, this.locale), "-", getFormattedDate(this.endDateValue, this.locale) );
  }

  private emitStartDateBlurEvent(event: FocusEvent) {
    if (
      this.invalidStartDate.length <= 9 &&
      this.invalidStartDate.length > 0 &&
      this.isEndInputFocused
    ) {
      this.isStartDateValid = false;
    } else {
      this.isStartDateValid = true;
    }

    this.handleBlur.emit(event);
  }

  private emitEndDateBlurEvent(event: FocusEvent) {
    if (this.invalidEndDate.length <= 9 && this.invalidEndDate.length > 0) {
      this.isEndDateValid = false;
    } else {
      this.isEndDateValid = true;
    }

    this.handleBlur.emit(event);
  }

  private emitFocusEvent(event: FocusEvent) {
    this.handleFocus.emit(event);
  }

  private handleClearDates(value: string) {
    let isSomeValueCleared = false;
    if ( value.length === 0 &&  (!this.isEndInputFocused || this.isStartDateInputHovered) ) {
      this.startDateValue = null;
      isSomeValueCleared = true;
    }

    if ( value.length === 0 &&  ((this.autoFillEndDate && !this.startDateValue) || this.isEndInputFocused || this.isEndDateInputHovered) ) {
      this.endDateValue = null;
      isSomeValueCleared = true;
    }

    if (isSomeValueCleared) {
      this.updatedDateValues();
    }
  }

  // private handleDateSelectedByKeyboard(event: Event) {
  //   const input = event.target as HTMLInputElement;

  //   // let cleanedValue = input.value.replace(/\//g, "");
  //   // input.value = getFormattedDateWithSlashes(cleanedValue);
  //   // this.handleClearDates(input.value);

  //   const rawValue = input.value;
  //   this.handleClearDates(rawValue);
  //   const cleanedValue = rawValue.replace(/\D/g, ''); // remove non-numeric
  //   if (cleanedValue.length === 8) {
  //     const formatted = getFormattedDateWithSlashes(cleanedValue);
  //     input.value = formatted;
  //   }

  //   if ( !isStringDateValid(input.value, this.locale) && input.value.length == 10 ) {
  //     const dateError = {
  //       message: `Invalid date entered: ${input.value}`,
  //       invalidDate: input.value,
  //       field: this.isEndInputFocused ? "endDate" : "startDate" as "endDate" | "startDate",
  //       timestamp: new Date(),
  //     };
  //     console.error(dateError);
  //     this.dateError.emit(dateError);

  //     if (this.isEndInputFocused) {
  //       this.isEndDateValid = false;
  //     } else {
  //       this.isStartDateValid = false;
  //     }
  //   } else {
  //     if (this.isEndInputFocused) {
  //       this.isEndDateValid = true;
  //     } else {
  //       this.isStartDateValid = true;
  //     }
  //   }

  //   if (isStringDateValid(input.value, this.locale)) {
  //     const { day, month, year } = getDateParts(input.value, this.locale);

  //     this.setDate(
  //       parseInt(day, 10),
  //       parseInt(month, 10) - 1,
  //       parseInt(year, 10)
  //     );

  //     this.invalidEndDate = "";
  //     this.invalidStartDate = "";
  //   } else {
  //     if (this.isEndInputFocused) {
  //       this.invalidEndDate = input.value;
  //     } else {
  //       this.invalidStartDate = input.value;
  //     }
  //   }
  // }

  private handleDateSelectedByKeyboard(event: Event) {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;
    const isEnd = this.isEndInputFocused;
  
    this.handleClearDates(rawValue);
  
    const cleanedValue = rawValue.replace(/\D/g, "");
  
    // ✅ Wait until all 8 digits are typed
    if (cleanedValue.length === 8) {
      const formatted = getFormattedDateWithSlashes(cleanedValue);
      input.value = formatted;
      
      if (isStringDateValid(formatted, this.locale)) {
        const { day, month, year } = getDateParts(formatted, this.locale);
        const parsedDay = parseInt(day, 10);
        const parsedMonth = parseInt(month, 10) - 1;
        const parsedYear = parseInt(year, 10);
        const date = new Date(parsedYear, parsedMonth, parsedDay);
  
        if (
          date.getFullYear() === parsedYear &&
          date.getMonth() === parsedMonth &&
          date.getDate() === parsedDay
        ) {
          if (isEnd) {
            this.endDateValue = date;
            this.invalidEndDate = "";
            this.isEndDateValid = true;
          } else {
            this.startDateValue = date;
            this.invalidStartDate = "";
            this.isStartDateValid = true;
          }
  
          this.runValidationRules();
          this.updatedDateValues();
        } else {
          this.markInvalidInput(input.value, isEnd);
        }
      } else {
        this.markInvalidInput(input.value, isEnd);
      }
    }
  }

  private handleDateBlur(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;
    const isEnd = this.isEndInputFocused;
  
    if (isStringDateValid(rawValue, this.locale)) {
      const { day, month, year } = getDateParts(rawValue, this.locale);
  
      const formatted =
        this.locale === "en-GB"
          ? `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`
          : `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${year}`;
  
      input.value = formatted;
  
      if (isEnd) {
        this.formattedEndDateValue = formatted;
      } else {
        this.formattedStartDateValue = formatted;
      }
    }
  
    // Mark validity just in case
    if (rawValue.length < 8 || !isStringDateValid(rawValue, this.locale)) {
      this.markInvalidInput(rawValue, isEnd);
    }
  
    // Emit blur event
    if (isEnd) {
      this.emitEndDateBlurEvent(event);
    } else {
      this.emitStartDateBlurEvent(event);
    }
  }
  
  // private markInvalidInput(value: string, isEnd: boolean) {
  //   const field = isEnd ? "endDate" : "startDate";
  //   const timestamp = new Date();
  
  //   if (isEnd) {
  //     this.invalidEndDate = value;
  //     this.isEndDateValid = false;
  //   } else {
  //     this.invalidStartDate = value;
  //     this.isStartDateValid = false;
  //   }
  
  //   this.dateError.emit({
  //     message: `Invalid date entered: ${value}`,
  //     invalidDate: value,
  //     field,
  //     timestamp,
  //   });
  // }

  private markInvalidInput(value: string, isEnd: boolean) {
    const field = isEnd ? "endDate" : "startDate";
    const timestamp = new Date();
  
    if (isEnd) {
      this.invalidEndDate = value;
      this.isEndDateValid = false;
    } else {
      this.invalidStartDate = value;
      this.isStartDateValid = false;
    }
  
    this.dateError.emit({
      message: `Invalid date entered: ${value}`,
      invalidDate: value,
      field,
      timestamp,
    });
  }

  private handleDateSelectedByUI(day: number, month: number, year: number) {
    const date = new Date(year, month, day);

    if (isDateDisabled(date, this.minDate, this.maxDate)) {
      return;
    }

    this.setDate(day, month, year);
  }

  private setDate(day: number, month: number, year: number) {
    const date = new Date(year, month, day);

    if (this.isEndInputFocused) {
      this.endDateValue = date;
    } else {
      this.startDateValue = date;
    }

    this.runValidationRules();
    this.updatedDateValues();

    if (!this.startDateValue) {
      this.startDateInputElement.focus();
    } else if (!this.endDateValue) {
      this.endDateInputElement.focus();
    } else {
      this.closeCalendar();
    }
  }

  private runValidationRules() {
    if (this.autoFillEndDate && !this.endDateValue) {
      this.endDateValue = this.startDateValue;
    }

    if (!this.startDateValue && this.endDateValue) {
      this.endDateValue = null;
    }

    if (this.startDateValue > this.endDateValue) {
      this.endDateValue = null;
    }
  }

  private handleInputFocus() {
    this.adjustCalendarContainerPosition();

    this.calendarContainerElement.style.visibility = "visible";
    this.showCalendar = true;
  }

  private adjustCalendarContainerPosition() {
    if (!this.startDateInputElement || !this.calendarContainerElement) {
      return;
    }

    moveContainerPosition(
      this.startDateInputElement,
      this.calendarContainerElement
    );
  }

  private closeCalendar() {
    this.calendarContainerElement.style.visibility = "hidden";
    this.showCalendar = false;
    this.view = "days";
  }

  private showStartDateClearButton() {
    return (
      this.clearable &&
      this.startDateValue &&
      this.isStartDateInputHovered &&
      !this.disabled
    );
  }

  private showEndDateClearButton() {
    return (
      this.clearable &&
      this.endDateValue &&
      this.isEndDateInputHovered &&
      !this.disabled
    );
  }

  private handleDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    if (!this.calendarContainerElement) {
      return;
    }

    if (
      !this.calendarContainerElement.contains(target) &&
      document.activeElement !== this.startDateInputElement &&
      document.activeElement !== this.endDateInputElement
    ) {
      this.closeCalendar();
    }
  }

  connectedCallback() {
    document.addEventListener("click", this.handleDocumentClick.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleDocumentClick.bind(this));
  }

  private handleDummyDate() {
    this.startDateValue = this.dummyDateValue;
    this.endDateValue = this.dummyDateValue;

    this.runValidationRules();
    this.updatedDateValues();
    this.closeCalendar();
  }

  private showPreviousYearRange() {
    this.yearRangeStart -= 10;
  }

  private showNextYearRange() {
    this.yearRangeStart += 10;
  }

  private selectYear(year: number) {
    this.currentYear = year;
    this.view = "months";
  }

  private showYearSelector() {
    this.view = "years";
  }

  private showMonthSelector() {
    this.view = "months";
  }

  private showNextYear() {
    this.currentYear++;
  }

  private showPreviousYear() {
    this.currentYear--;
  }

  private selectMonth(monthIndex: number) {
    this.currentMonth = monthIndex;
    this.view = "days";
  }

  render() {
    const startDateInputClassNames = {
      disabled: this.disabled,
      "start-date-error": this.error || this.isStartDateValid == false,
      warning: this.warning,
    };

    const endDateInputClassNames = {
      disabled: this.disabled,
      "end-date-error": this.error || this.isEndDateValid == false,
      warning: this.warning,
    };

    const daysInCurrentMonth = daysInMonth(this.currentMonth, this.currentYear);
    const daysInNextMonth = getDaysInNextMonth(
      this.currentMonth,
      this.currentYear
    );
    const firstDayOfMonth = getFirstDayOfMonth(
      this.currentMonth,
      this.currentYear
    );
    const lastDayOfMonth = getLastDayOfMonth(
      this.currentMonth,
      this.currentYear
    );
    const yearRange = Array.from(
      { length: 10 },
      (_, index) => this.yearRangeStart + index
    );

    return (
      <Host>
        <div class="signet-date-range">
          <div class="input-container">
            <div
              class="input-label-container"
              onMouseEnter={() => {
                this.isStartDateInputHovered = true;
                this.isEndDateInputHovered = false;
              }}
              onMouseLeave={() => (this.isStartDateInputHovered = false)}
            >
              <label htmlFor="date" class="start-date-input-label">
                {this.startDateLabel}
                {this.required && (
                  <span style={{ color: this.requiredAsteriskColor, marginLeft: '2px' }}>*</span>
                )}
              </label>
              <input
                type="text"
                ref={(e) =>
                  (this.startDateInputElement = e as HTMLInputElement)
                }
                value={this.formattedStartDateValue}
                onInput={(evt) => {
                  this.handleDateSelectedByKeyboard(evt);
                }}
                onFocus={(evt) => {
                  this.handleInputFocus();
                  this.emitFocusEvent(evt);
                  this.isEndInputFocused = false;
                }}
                onBlur={(evt) => this.handleDateBlur(evt)}
                // onBlur={(evt) => this.emitStartDateBlurEvent(evt)}
                placeholder={this.placeholder}
                disabled={this.disabled}
                class={{
                  ...startDateInputClassNames,
                  'input-size-small': this.getNormalizedSize() === 'small',
                  'input-size-medium': this.getNormalizedSize() === 'medium',
                  'input-size-large': this.getNormalizedSize() === 'large',
                }}
              />
              {this.showStartDateClearButton() && (
                <span
                  class="input-clear-button"
                  onClick={() => this.handleClearDates("")}
                >
                  &#x2715;
                </span>
              )}
            </div>
            <span>&ndash;</span>
            <div
              class="input-label-container"
              onMouseEnter={() => {
                this.isEndDateInputHovered = true;
                this.isStartDateInputHovered = false;
              }}
              onMouseLeave={() => (this.isEndDateInputHovered = false)}
            >
              <label htmlFor="date" class="end-date-input-label">
                {this.endDateLabel}
                {this.required && (
                  <span style={{ color: this.requiredAsteriskColor, marginLeft: '2px' }}>*</span>
                )}
              </label>
              <input
                type="text"
                ref={(e) => (this.endDateInputElement = e as HTMLInputElement)}
                value={this.formattedEndDateValue}
                onInput={(evt) => {
                  this.handleDateSelectedByKeyboard(evt);
                }}
                onFocus={(evt) => {
                  this.handleInputFocus();
                  this.emitFocusEvent(evt);
                  this.isEndInputFocused = true;
                }}
                // onBlur={(evt) => {
                //   this.emitEndDateBlurEvent(evt);
                // }}
                onBlur={(evt) => this.handleDateBlur(evt)}
                placeholder={this.placeholder}
                disabled={this.disabled}
                class={{
                  ...endDateInputClassNames,
                  'input-size-small': this.getNormalizedSize() === 'small',
                  'input-size-medium': this.getNormalizedSize() === 'medium',
                  'input-size-large': this.getNormalizedSize() === 'large',
                }}
              />
              {this.showEndDateClearButton() && (
                <span
                  class="input-clear-button"
                  onClick={() => this.handleClearDates("")}
                >
                  &#x2715;
                </span>
              )}
            </div>
          </div>

          <div
            class="calendar-container-wrapper"
            ref={(e) => (this.calendarContainerElement = e as HTMLElement)}
            style={{ visibility: "hidden" }}
          >
            {this.dummyDate ? (
              <div class="shortcuts">
                <div
                  class="shortcut-button"
                  onClick={() => this.handleDummyDate()}
                >
                  Dummy Date
                </div>
              </div>
            ) : (
              ""
            )}
            <div class="calendar-container-content">
              {this.view === "years" ? (
                <div>
                  <div class="year-selector">
                    <signet-icon
                      class="calendar-icon left"
                      icon="chevron_left"
                      onClick={() => this.showPreviousYearRange()}
                    />
                    <span class="year-range">
                      {this.yearRangeStart} - {this.yearRangeStart + 9}
                    </span>
                    <signet-icon
                      class="calendar-icon right"
                      icon="chevron_right"
                      onClick={() => this.showNextYearRange()}
                    />
                  </div>
                  <div class="year-selector-content">
                    {yearRange.map((year) => (
                      <div
                        class="year-item"
                        onClick={() => this.selectYear(year)}
                      >
                        {year}
                      </div>
                    ))}
                  </div>
                </div>
              ) : this.view === "months" ? (
                <div>
                  <div class="year-selector">
                    <signet-icon
                      class="calendar-icon left"
                      icon="chevron_left"
                      onClick={() => this.showPreviousYear()}
                    />
                    <span class="year">{this.currentYear}</span>
                    <signet-icon
                      class="calendar-icon right"
                      icon="chevron_right"
                      onClick={() => this.showNextYear()}
                    />
                  </div>
                  <div class="month-selector">
                    {monthNames.map((month, index) => (
                      <div
                        class="month-item"
                        onClick={() => this.selectMonth(index)}
                      >
                        {month}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div class="calendar">
                  <signet-icon
                    class="calendar-icon left"
                    icon="chevron_left"
                    onClick={() => this.showPreviousMonth()}
                  />
                  <h2 class="month-year">
                    <span
                      class="month"
                      onClick={() => this.showMonthSelector()}
                    >
                      {monthNames[this.currentMonth]}
                    </span>{" "}
                    <span class="year" onClick={() => this.showYearSelector()}>
                      {this.currentYear}
                    </span>
                  </h2>
                  <div class="weekdays">
                    {weekDays.map((day) => (
                      <div class="weekday">{day}</div>
                    ))}
                  </div>
                  <div class="days">
                    {Array.from({ length: firstDayOfMonth }, (_, index) => (
                      <div class="day blank" key={index}></div>
                    ))}
                    {Array.from({ length: daysInCurrentMonth }, (_, index) => {
                      const day = index + 1;
                      const date = new Date(
                        this.currentYear,
                        this.currentMonth,
                        day
                      );

                      const isSelected =
                        (this.startDateValue &&
                          this.endDateValue &&
                          date >= this.startDateValue &&
                          date <= this.endDateValue) ||
                        (!this.endDateValue &&
                          this.startDateValue &&
                          date.getTime() === this.startDateValue.getTime());
                      const isBetween =
                        this.startDateValue &&
                        this.endDateValue &&
                        date > this.startDateValue &&
                        date < this.endDateValue;
                      const isSelectedFirstDay =
                        this.startDateValue &&
                        date.getMonth() === this.startDateValue.getMonth() &&
                        date.getFullYear() ===
                          this.startDateValue.getFullYear() &&
                        date.getDate() === this.startDateValue.getDate();
                      const isFirstDayAfterStart =
                        this.startDateValue &&
                        date.getMonth() === this.startDateValue.getMonth() &&
                        date.getFullYear() ===
                          this.startDateValue.getFullYear() &&
                        date.getDate() === this.startDateValue.getDate() + 1;
                      const isSelectedLastDay =
                        this.endDateValue &&
                        date.getMonth() === this.endDateValue.getMonth() &&
                        date.getFullYear() ===
                          this.endDateValue.getFullYear() &&
                        date.getDate() === this.endDateValue.getDate();
                      const isLastDayBeforeEnd =
                        this.endDateValue &&
                        date.getMonth() === this.endDateValue.getMonth() &&
                        date.getFullYear() ===
                          this.endDateValue.getFullYear() &&
                        date.getDate() === this.endDateValue.getDate() - 1;
                      return (
                        <div
                          class={`day ${
                            isCurrentMonth(
                              day,
                              this.currentMonth,
                              this.currentYear
                            )
                              ? "current-day"
                              : ""
                          } ${isSelected ? "selected-day" : ""}
                          
                          ${isSelectedFirstDay ? "selected-first-day" : ""}
                          
                          ${isSelectedLastDay ? "selected-last-day" : ""}

                          ${isBetween ? "between-day" : ""} 
                          
                          ${
                            isFirstDayAfterStart ? "first-day-after-start" : ""
                          } 
                          
                          ${isLastDayBeforeEnd ? "last-day-before-end" : ""} 
                          
                          ${
                            isDateDisabled(
                              new Date(
                                this.currentYear,
                                this.currentMonth,
                                day
                              ),
                              this.minDate,
                              this.maxDate
                            )
                              ? "disabled-day"
                              : ""
                          }`}
                          key={day}
                          data-day={day}
                          data-month={this.currentMonth}
                          data-year={this.currentYear}
                          onClick={() =>
                            this.handleDateSelectedByUI(
                              day,
                              this.currentMonth,
                              this.currentYear
                            )
                          }
                        >
                          {day}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {this.view == "years" ? (
                <span></span>
              ) : this.view == "months" ? (
                <span></span>
              ) : (
                <div class="divider" />
              )}

              {this.view === "years" ? (
                <span></span>
              ) : this.view === "months" ? (
                <span></span>
              ) : (
                <div class="calendar">
                  <h2 class="month-year">
                    <span
                      class="month"
                      onClick={() => this.showMonthSelector()}
                    >
                      {monthNames[(this.currentMonth + 1) % 12]}
                    </span>{" "}
                    <span class="year" onClick={() => this.showYearSelector()}>
                      {this.currentMonth === 11
                        ? this.currentYear + 1
                        : this.currentYear}
                    </span>
                  </h2>

                  <div class="weekdays">
                    {weekDays.map((day) => (
                      <div class="weekday">{day}</div>
                    ))}
                  </div>
                  <div class="days">
                    {Array.from(
                      { length: lastDayOfMonth === 6 ? 0 : lastDayOfMonth + 1 },
                      (_, index) => (
                        <div class="day blank" key={index}></div>
                      )
                    )}
                    {Array.from({ length: daysInNextMonth }, (_, index) => {
                      const day = index + 1;
                      const date = new Date(
                        this.currentYear,
                        this.currentMonth + 1,
                        day
                      );
                      const isSelected =
                        (this.startDateValue &&
                          this.endDateValue &&
                          date >= this.startDateValue &&
                          date <= this.endDateValue) ||
                        (!this.endDateValue &&
                          this.startDateValue &&
                          date.toDateString() ===
                            this.startDateValue.toDateString());
                      const isBetween =
                        this.startDateValue &&
                        this.endDateValue &&
                        date > this.startDateValue &&
                        date < this.endDateValue;

                      const isSelectedFirstDay =
                        this.startDateValue &&
                        date.getMonth() === this.startDateValue.getMonth() &&
                        date.getFullYear() ===
                          this.startDateValue.getFullYear() &&
                        date.getDate() === this.startDateValue.getDate();

                      const isSelectedLastDay =
                        this.endDateValue &&
                        date.getMonth() === this.endDateValue.getMonth() &&
                        date.getFullYear() ===
                          this.endDateValue.getFullYear() &&
                        date.getDate() === this.endDateValue.getDate();
                      const isFirstDayAfterStart =
                        this.startDateValue &&
                        date.getMonth() === this.startDateValue.getMonth() &&
                        date.getFullYear() ===
                          this.startDateValue.getFullYear() &&
                        date.getDate() === this.startDateValue.getDate() + 1;
                      const isLastDayBeforeEnd =
                        this.endDateValue &&
                        date.getMonth() === this.endDateValue.getMonth() &&
                        date.getFullYear() ===
                          this.endDateValue.getFullYear() &&
                        date.getDate() === this.endDateValue.getDate() - 1;
                      return (
                        <div
                          class={`day ${
                            isCurrentMonth(
                              index + 1,
                              (this.currentMonth + 1) % 12,
                              this.currentMonth === 11
                                ? this.currentYear + 1
                                : this.currentYear
                            )
                              ? "current-day"
                              : ""
                          } ${isSelected ? "selected-day" : ""} ${
                            isBetween ? "between-day" : ""
                          } 
                          
                          ${isSelectedFirstDay ? "selected-first-day" : ""}
                          
                          ${isSelectedLastDay ? "selected-last-day" : ""}
                          
                          ${
                            isFirstDayAfterStart ? "first-day-after-start" : ""
                          } ${
                            isLastDayBeforeEnd ? "last-day-before-end" : ""
                          } ${
                            isDateDisabled(
                              new Date(
                                this.currentYear,
                                this.currentMonth + 1,
                                day
                              ),
                              this.minDate,
                              this.maxDate
                            )
                              ? "disabled-day"
                              : ""
                          }`}
                          key={day}
                          data-day={day}
                          data-month={this.currentMonth + 1}
                          data-year={this.currentYear}
                          onClick={() =>
                            this.handleDateSelectedByUI(
                              day,
                              this.currentMonth + 1,
                              this.currentYear
                            )
                          }
                        >
                          {day}
                        </div>
                      );
                    })}
                  </div>
                  <signet-icon
                    class="calendar-icon right"
                    icon="chevron_right"
                    onClick={() => this.showNextMonth()}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
